import React from "react";

import { HStack, VStack } from "@UI/Components/Stack/Stack";
import { Heading } from "@UI/Components/Heading/Heading";
import { Text } from "@UI/Components/Text/Text";
import { Box } from "@UI/Components/Box/Box";
import { Link } from "@UI/Components/Link/Link";
import { Icon } from "@UI/Components/Icon/Icon";
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
} from "@UI/Components/Accordion/Accordion";
import { Menu, MenuButton, MenuItem, MenuList } from "@UI/Components/Menu/Menu";
import { Plus } from "@UI/Components/Icons/Plus";
import { Minus } from "@UI/Components/Icons/Minus";
import { ThreeDots } from "@UI/Components/Icons/ThreeDots";
import { useServiceRequestFlow } from "../../hooks/useServiceRequestFlow";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import messages from "../messages";
import { ExpandedServiceRequestDetails } from "./ExpandedServiceRequestDetails";
import { useDisclosure } from "@UI/hooks/useDisclosure";
import { useNavigate } from "@Libs/Router/hooks/useNavigate";
import { useServiceRequestCandidates } from "@modules/ServiceRequest/hooks/useCandidates";
import { ConfirmationModal } from "@Shared/Components/ConfirmationModal/ConfirmationModal";
import { Tooltip } from "@UI/Components/Tooltip/Tooltip";
import useI18n from "@I18n/hooks/useI18n";

export const ServiceRequestDetailCard = () => {
  const { serviceRequest } = useServiceRequestFlow();
  const { formatMessage } = useI18n();
  const { isOpen, onToggle } = useDisclosure();
  const {
    isOpen: isCancelConfirmationOpen,
    onOpen: onCancelConfirmationOpen,
    onClose: onCancelConfirmationClose,
  } = useDisclosure();
  const navigate = useNavigate();

  const { candidates = [] } = useServiceRequestCandidates({
    serviceRequestId: serviceRequest.id,
    sortBy: "recommended",
  });

  const serviceRequestHasOffers = candidates?.some(
    (candidates) => candidates?.serviceOffer
  );

  const handelServiceRequestCancellation = () => {
    navigate(
      serviceRequestHasOffers
        ? "LEGACY_HAS_CONTRACT_TASK_CANCEL"
        : "LEGACY_NO_CONTRACT_TASK_CANCEL",
      {
        reload: true,
        queryParams: { id: String(serviceRequest.id) },
      }
    );
  };

  return (
    <VStack
      w="full"
      alignItems="start"
      spacing="18px"
      background="white"
      p={{ base: "16px", lg: "24px" }}
      borderRadius="10px"
    >
      <HStack w="full" spacing="8px" justifyContent="space-between">
        <Tooltip label={serviceRequest.title}>
          <Heading
            fontSize={{ base: "1.625rem", md: "2.25rem" }}
            _firstLetter={{ textTransform: "uppercase" }}
            noOfLines={2}
          >
            {serviceRequest.title}
          </Heading>
        </Tooltip>
        <Menu>
          <MenuButton
            _hover={{ background: "gray.100" }}
            width="32px"
            height="32px"
            borderRadius="10px"
            alignSelf="start"
            // alignItems="start"
          >
            <Icon
              cursor="pointer"
              as={ThreeDots}
              width="16px"
              height="16px"
              color="gray.700"
              _hover={{ color: "gray.800" }}
            />
          </MenuButton>
          <MenuList minWidth="79px">
            <MenuItem
              onClick={() => {
                navigate("LEGACY_SERVICE_REQUEST_EDIT", {
                  reload: true,
                  params: { id: String(serviceRequest.id) },
                });
              }}
            >
              <FormattedMessage {...messages["edit"]} />
            </MenuItem>
            <MenuItem
              onClick={
                serviceRequest?.appliedServiceProvidersCount < 1
                  ? onCancelConfirmationOpen
                  : handelServiceRequestCancellation
              }
            >
              <FormattedMessage
                {...messages["serviceRequest.declineOffer.cancelButton.text"]}
              />
            </MenuItem>
          </MenuList>
        </Menu>
        <ConfirmationModal
          title={formatMessage(messages["serviceRequest.cancellation.title"])}
          message={formatMessage(
            messages["serviceRequest.cancellation.message"]
          )}
          actionText={formatMessage(
            messages["serviceRequest.cancellation.confirmCTAText"]
          )}
          cancelActionText={formatMessage(
            messages["serviceRequest.cancellation.cancelCTAText"]
          )}
          isOpen={isCancelConfirmationOpen}
          onClose={onCancelConfirmationClose}
          onAction={handelServiceRequestCancellation}
        />
      </HStack>
      <Box
        display={{ base: "initial", lg: "none" }}
        py="2px"
        px="8px"
        bg="orange.50"
        borderRadius="1000px"
      >
        <HStack spacing="8px">
          <Box w="12px" h="12px" bg="orange.500" borderRadius="50%" />
          {serviceRequest?.state === "posted" && (
            <Text
              color="gray.900"
              fontWeight="700"
              fontSize={{ base: "0.875rem", md: "1rem" }}
            >
              <FormattedMessage
                {...messages[`serviceRequestStateType.${serviceRequest.state}`]}
              />
            </Text>
          )}
        </HStack>
      </Box>

      <Accordion allowToggle border="none">
        <AccordionItem border="none">
          <AccordionPanel p="0" pb={4}>
            <ExpandedServiceRequestDetails />
          </AccordionPanel>
          <AccordionButton p="0">
            <Link
              h="fit-content"
              textDecoration="underline"
              color="gray.700"
              onClick={onToggle}
              _hover={{ color: "gray.800" }}
            >
              <Icon as={isOpen ? Minus : Plus} width="16px" height="16px" />
              <Text m="0" ml="4px" display="inline">
                <FormattedMessage
                  {...messages[isOpen ? "hideDetails" : "seeDetails"]}
                />
              </Text>
            </Link>
          </AccordionButton>
        </AccordionItem>
      </Accordion>
    </VStack>
  );
};
